import React, { useState, useEffect } from "react";
import Icon from "components/AppIcon";
import api from "../../../lib/api";
import { useAuth } from "../../../contexts/AuthContext";

const ICON_OPTIONS = [
  "Tag", "Home", "Zap", "Users", "Dumbbell", "Wrench", "Megaphone",
  "ShoppingCart", "Shield", "Car", "Coffee", "Wifi", "Phone", "Printer",
  "Package", "Briefcase", "CreditCard", "DollarSign", "TrendingUp",
];

const COLOR_OPTIONS = [
  "#ef4444", "#f97316", "#eab308", "#22c55e", "#06b6d4",
  "#8b5cf6", "#ec4899", "#14b8a6", "#6b7280", "#3b82f6",
  "#a855f7", "#f43f5e", "#84cc16", "#0ea5e9", "#d97706",
];

function CategoryModal({ open, onClose, onSaved, editData }) {
  const [form, setForm] = useState({ name: "", color: "#6366f1", icon: "Tag" });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (editData) {
      setForm({ name: editData?.name || "", color: editData?.color || "#6366f1", icon: editData?.icon || "Tag" });
    } else {
      setForm({ name: "", color: "#6366f1", icon: "Tag" });
    }
    setError(null);
  }, [editData, open]);

  const handleSave = async () => {
    if (!form?.name?.trim()) { setError("Category name is required."); return; }
    setSaving(true);
    try {
      await api.post('/expenses/categories', { name: form?.name?.trim(), color: form?.color, icon: form?.icon });
      onSaved();
      onClose();
    } catch (err) {
      setError(err?.message || "Failed to save category.");
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-[var(--color-card)] rounded-[var(--radius)] border border-[var(--color-border)] w-full max-w-md shadow-xl">
        <div className="flex items-center justify-between px-5 py-4 border-b border-[var(--color-border)]">
          <h3 className="text-base font-bold font-[var(--font-heading)] text-[var(--color-foreground)]">
            {editData ? "Edit Category" : "Add Category"}
          </h3>
          <button onClick={onClose} className="p-1.5 rounded-md hover:bg-[var(--color-muted)] transition-colors">
            <Icon name="X" size={16} color="var(--color-muted-foreground)" />
          </button>
        </div>
        <div className="p-5 space-y-4">
          {error && (
            <div className="flex items-center gap-2 p-3 bg-[var(--color-error)]/10 border border-[var(--color-error)]/20 rounded-md">
              <Icon name="AlertCircle" size={14} color="var(--color-error)" />
              <p className="text-xs text-[var(--color-error)]">{error}</p>
            </div>
          )}
          <div>
            <label className="block text-xs font-medium text-[var(--color-muted-foreground)] mb-1.5">Category Name *</label>
            <input
              value={form?.name}
              onChange={e => setForm(p => ({ ...p, name: e?.target?.value }))}
              placeholder="e.g. Cleaning Services"
              className="w-full px-3 py-2 text-sm border border-[var(--color-border)] rounded-md bg-[var(--color-input)] text-[var(--color-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--color-ring)]"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-[var(--color-muted-foreground)] mb-1.5">Color</label>
            <div className="flex flex-wrap gap-2">
              {COLOR_OPTIONS?.map(c => (
                <button
                  key={c}
                  onClick={() => setForm(p => ({ ...p, color: c }))}
                  className="w-7 h-7 rounded-full border-2 transition-all"
                  style={{ backgroundColor: c, borderColor: form?.color === c ? "var(--color-foreground)" : "transparent" }}
                />
              ))}
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-[var(--color-muted-foreground)] mb-1.5">Icon</label>
            <div className="flex flex-wrap gap-2">
              {ICON_OPTIONS?.map(ic => (
                <button
                  key={ic}
                  onClick={() => setForm(p => ({ ...p, icon: ic }))}
                  className={`w-8 h-8 rounded-md flex items-center justify-center border transition-all ${form?.icon === ic ? "border-[var(--color-primary)] bg-[var(--color-primary)]/10" : "border-[var(--color-border)] hover:bg-[var(--color-muted)]"}`}
                >
                  <Icon name={ic} size={14} color={form?.icon === ic ? "var(--color-primary)" : "var(--color-muted-foreground)"} />
                </button>
              ))}
            </div>
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-[var(--color-border)]">
          <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-[var(--color-muted-foreground)] hover:text-[var(--color-foreground)] transition-colors">Cancel</button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium bg-[var(--color-primary)] text-white rounded-md hover:bg-[var(--color-secondary)] transition-colors disabled:opacity-50"
          >
            {saving ? <Icon name="Loader2" size={14} color="white" className="animate-spin" /> : <Icon name="Check" size={14} color="white" />}
            {saving ? "Saving..." : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function ExpenseCategories({ onCategoriesChange }) {
  useAuth();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editData, setEditData] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const fetchCategories = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/expenses/categories');
      setCategories(data || []);
    } catch (err) {
      console.error("Error fetching categories:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchCategories(); }, []);

  const handleSaved = () => {
    fetchCategories();
    onCategoriesChange?.();
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    setDeleting(true);
    try {
      await api.delete(`/expenses/categories/${deleteId}`);
      setDeleteId(null);
      fetchCategories();
      onCategoriesChange?.();
    } catch (err) {
      console.error("Delete error:", err);
    } finally {
      setDeleting(false);
    }
  };

  const predefined = categories?.filter(c => c?.is_predefined);
  const custom = categories?.filter(c => !c?.is_predefined);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-bold font-[var(--font-heading)] text-[var(--color-foreground)]">Expense Categories</h3>
          <p className="text-xs text-[var(--color-muted-foreground)] mt-0.5">Manage categories to organize your expenses</p>
        </div>
        <button
          onClick={() => { setEditData(null); setModalOpen(true); }}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-[var(--color-primary)] text-white rounded-md hover:bg-[var(--color-secondary)] transition-colors"
        >
          <Icon name="Plus" size={13} color="white" />
          Add Category
        </button>
      </div>

      {loading ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
          {[...Array(9)]?.map((_, i) => (
            <div key={i} className="h-20 bg-[var(--color-muted)] rounded-[var(--radius-sm)] animate-pulse" />
          ))}
        </div>
      ) : (
        <>
          <div>
            <p className="text-xs font-medium text-[var(--color-muted-foreground)] mb-2 uppercase tracking-wide">Predefined</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
              {predefined?.map(cat => (
                <div key={cat?.id} className="flex flex-col items-center gap-2 p-3 bg-[var(--color-muted)]/40 rounded-[var(--radius-sm)] border border-[var(--color-border)]">
                  <div className="w-9 h-9 rounded-full flex items-center justify-center" style={{ backgroundColor: `${cat?.color}22` }}>
                    <Icon name={cat?.icon} size={16} color={cat?.color} />
                  </div>
                  <span className="text-xs font-medium text-[var(--color-foreground)] text-center leading-tight">{cat?.name}</span>
                  <span className="text-xs text-[var(--color-muted-foreground)]">Predefined</span>
                </div>
              ))}
            </div>
          </div>

          {custom?.length > 0 && (
            <div>
              <p className="text-xs font-medium text-[var(--color-muted-foreground)] mb-2 uppercase tracking-wide">Custom</p>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                {custom?.map(cat => (
                  <div key={cat?.id} className="flex flex-col items-center gap-2 p-3 bg-[var(--color-muted)]/40 rounded-[var(--radius-sm)] border border-[var(--color-border)] relative group">
                    <div className="w-9 h-9 rounded-full flex items-center justify-center" style={{ backgroundColor: `${cat?.color}22` }}>
                      <Icon name={cat?.icon} size={16} color={cat?.color} />
                    </div>
                    <span className="text-xs font-medium text-[var(--color-foreground)] text-center leading-tight">{cat?.name}</span>
                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button
                        onClick={() => { setEditData(cat); setModalOpen(true); }}
                        className="p-1 rounded hover:bg-[var(--color-muted)] transition-colors"
                      >
                        <Icon name="Pencil" size={11} color="var(--color-muted-foreground)" />
                      </button>
                      <button
                        onClick={() => setDeleteId(cat?.id)}
                        className="p-1 rounded hover:bg-[var(--color-error)]/10 transition-colors"
                      >
                        <Icon name="Trash2" size={11} color="var(--color-error)" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {custom?.length === 0 && (
            <div className="flex flex-col items-center gap-2 py-8 text-center">
              <div className="w-10 h-10 rounded-full bg-[var(--color-muted)] flex items-center justify-center">
                <Icon name="FolderPlus" size={18} color="var(--color-muted-foreground)" />
              </div>
              <p className="text-sm text-[var(--color-muted-foreground)]">No custom categories yet</p>
              <button
                onClick={() => { setEditData(null); setModalOpen(true); }}
                className="text-xs text-[var(--color-primary)] hover:underline"
              >
                Add your first custom category
              </button>
            </div>
          )}
        </>
      )}

      <CategoryModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSaved={handleSaved}
        editData={editData}
      />

      {/* Delete Confirm */}
      {deleteId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-[var(--color-card)] rounded-[var(--radius)] border border-[var(--color-border)] w-full max-w-sm p-6 shadow-xl">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-[var(--color-error)]/10 flex items-center justify-center">
                <Icon name="Trash2" size={18} color="var(--color-error)" />
              </div>
              <div>
                <h4 className="text-sm font-bold text-[var(--color-foreground)]">Delete Category</h4>
                <p className="text-xs text-[var(--color-muted-foreground)]">This action cannot be undone.</p>
              </div>
            </div>
            <p className="text-sm text-[var(--color-muted-foreground)] mb-5">Expenses in this category will have their category cleared.</p>
            <div className="flex items-center justify-end gap-2">
              <button onClick={() => setDeleteId(null)} className="px-4 py-2 text-sm font-medium text-[var(--color-muted-foreground)] hover:text-[var(--color-foreground)] transition-colors">Cancel</button>
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium bg-[var(--color-error)] text-white rounded-md hover:opacity-90 transition-opacity disabled:opacity-50"
              >
                {deleting ? <Icon name="Loader2" size={14} color="white" className="animate-spin" /> : <Icon name="Trash2" size={14} color="white" />}
                {deleting ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
